import { useMemo } from 'react'
import { Flex, Link, Text } from '@radix-ui/themes'
import type { Ops } from '@willie/schemas'
import { SectionCard } from '~/components/SectionCard'
import { TicketRow, daysAgoLabel } from '~/features/freshdesk/components/TicketRow'

type OpenTicket = Ops.Freshdesk.OpenTicket

interface OrgTicketsCardProps {
  tickets: OpenTicket[]
  siteNameByInstallationId: Map<string, string>
  onInstallationClick: (installationId: string) => void
}

export const OrgTicketsCard = ({
  tickets,
  siteNameByInstallationId,
  onInstallationClick
}: OrgTicketsCardProps) => {
  // Oldest first: this is an attention panel, so the tickets that have been sitting
  // longest belong at the top.
  const ordered = useMemo(() => [...tickets].sort((a, b) => a.createdAt.localeCompare(b.createdAt)), [tickets])

  return (
    <SectionCard
      title="Open support tickets"
      badge={ordered.length === 0 ? 'None open' : `🎫 ${ordered.length}`}
      badgeColor={ordered.length === 0 ? 'green' : 'orange'}
    >
      <Flex direction="column" gap="3">
        {ordered.map((ticket) => (
          <TicketRow
            key={ticket.id}
            ticket={ticket}
            footer={
              <Flex align="center" gap="2" wrap="wrap">
                <Link
                  size="1"
                  onClick={() => onInstallationClick(ticket.installationId)}
                  className="cursor-pointer font-mono"
                >
                  #{ticket.installationId}
                </Link>
                <Text size="1" color="gray">
                  {siteNameByInstallationId.get(ticket.installationId) ?? '—'} · Opened{' '}
                  {daysAgoLabel(ticket.createdAt)} · Last updated {daysAgoLabel(ticket.updatedAt)}
                </Text>
              </Flex>
            }
          />
        ))}
      </Flex>
    </SectionCard>
  )
}
